import React from "react";
import { Match, MATCH_STATUS } from "../../../types";

// Classe colore del badge di stato, condivisa tra lista match e calendario.
const statusBadgeClassName = (match: Match) => {
  if (match.status === "scheduled") return "bg-green-200 text-green-800";
  if (match.status === "ongoing") return "bg-yellow-200 text-yellow-800";
  return "bg-blue-200 text-blue-800";
};

// Testo del punteggio se il match è stato disputato, altrimenti null.
export const matchScoreText = (match: Match) =>
  match.score1 != null && match.score2 != null ? `${match.score1} - ${match.score2}` : null;

interface MatchCardProps {
  match: Match;
  // Etichette già risolte (nome partecipante o slot/"TBD") decise dal chiamante.
  player1Label: string;
  player2Label: string;
  // Testo in alto a sinistra (orario o range); se assente usa `#order`.
  timeLabel?: string | null;
  // Piccola etichetta grigia accanto all'orario (es. nome stage nella lista).
  stageLabel?: string | null;
  // Riga di sottotitolo in fondo (es. "Torneo · Stage" nel calendario).
  subtitle?: string | null;
  // Badge aggiuntivo a sinistra di quello di stato (es. "overlap" nel calendario).
  extraBadge?: React.ReactNode;
  // Pulsanti azione a destra (lista match); se presenti la card diventa orizzontale.
  actions?: React.ReactNode;
  // Se passato la card è cliccabile (button) — usato dal calendario per aprire la modale.
  onClick?: () => void;
  // Classi aggiuntive del contenitore: padding/testo/bordo/posizionamento per-uso.
  className?: string;
  style?: React.CSSProperties;
}

// La base contiene SOLO classi strutturali: padding, dimensione testo, colore
// bordo, sfondo e posizionamento arrivano da `className` così i due usi (riga
// larga con azioni vs card compatta del time-grid) non vanno in conflitto.
const BASE_CLASS = "flex flex-col gap-2 rounded border text-left";

export const MatchCard: React.FC<MatchCardProps> = ({
  match,
  player1Label,
  player2Label,
  timeLabel,
  stageLabel,
  subtitle,
  extraBadge,
  actions,
  onClick,
  className = "p-3 text-sm border-gray-300",
  style,
}) => {
  const scoreText = matchScoreText(match);
  const statusText = scoreText ?? MATCH_STATUS[match.status];
  const horizontal = Boolean(actions);
  const clickable = Boolean(onClick);
  const Wrapper: React.ElementType = clickable ? "button" : "div";

  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      onClick={onClick}
      style={style}
      className={`${BASE_CLASS} ${horizontal ? "sm:flex-row sm:items-center sm:justify-between" : ""} ${
        clickable ? "transition-colors" : ""
      } ${className}`}
    >
      <div className="min-w-0">
        <div className="flex justify-between gap-2">
          <div className="font-semibold text-gray-900">
            {timeLabel ?? `#${match.order ?? match.id}`}
            {stageLabel ? (
              <span className="ml-2 text-xs font-normal text-gray-500">{stageLabel}</span>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            {extraBadge}
            <span
              className={`inline-flex w-fit rounded px-2 py-1 text-xs font-semibold ${statusBadgeClassName(match)}`}
            >
              {statusText}
            </span>
          </div>
        </div>
        <div className="mt-2 font-medium text-gray-800">
          <span className="font-semibold">{player1Label}</span>
          <span className="px-2 text-gray-500">vs</span>
          <span className="font-semibold">{player2Label}</span>
        </div>
        {subtitle ? <div className="mt-1 truncate text-xs text-gray-500">{subtitle}</div> : null}
      </div>
      {actions ? <div className="flex justify-between gap-2">{actions}</div> : null}
    </Wrapper>
  );
};
